/**
 * The arb rule itself, and the only copy of it.
 *
 * Two places need it and neither can be the owner: the grid shades cells in the browser on every repaint,
 * which cannot wait for a round trip, and the recorder raises notifications on the server, where no
 * browser is involved. So the rule lives here in CommonJS, which the react build imports and node
 * requires unchanged. It is deployed alongside server/ for that reason — see the deploy notes.
 *
 * Keep this file free of anything browser or node specific: no fetch, no fs, no JSX.
 */
const SOFT_BOOKMAKER = 'Bovada'
const SHARP_BOOKMAKER = 'Pinnacle'

const isSoftRow = (row) => Boolean(row) && String(row.bookmaker || '').startsWith(SOFT_BOOKMAKER)
const isSharpRow = (row) => Boolean(row) && String(row.bookmaker || '').startsWith(SHARP_BOOKMAKER)

// the odds comparison service still appends "(demo)" until it is redeployed, it has no business on screen
const displayBookmaker = (name) => String(name || '').replace(/\s*\(demo\)\s*$/i, '')

const findSoftRow = (bookieList) => (bookieList || []).find(isSoftRow) || null
const findSharpRow = (bookieList) => (bookieList || []).find(isSharpRow) || null

const usable = (price) => typeof price === 'number' && price > 1
const sameLine = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && a === b

/**
 * One market: every outcome has to be covered, and each outcome is taken wherever it pays best.
 * Backing the whole set costs sum(1/price); below 1 the payout beats the outlay no matter who wins,
 * and what is left over is the profit as a share of the total stake.
 */
const marketArb = (outcomes) => {
  const legs = []

  for (const outcome of outcomes) {
    const candidates = outcome.candidates.filter((c) => usable(c.price))
    if (!candidates.length) {
      return null
    }
    const best = candidates.reduce((a, b) => (b.price > a.price ? b : a))
    legs.push({ ...best, outcome: outcome.name })
  }

  const outlay = legs.reduce((sum, leg) => sum + 1 / leg.price, 0)
  const profit = (1 - outlay) * 100

  // a one sided set is not an arb, both books quoting the same leg is not a hedge
  const books = new Set(legs.map((leg) => leg.bookmaker))
  if (books.size < 2) {
    return null
  }

  return { profit, legs }
}

/**
 * Only Bovada against Pinnacle for now. Betfair is left out on purpose: its quotes are lay prices and
 * the outlay of a lay bet is not 1/price, so it needs its own formula.
 */
const findArbs = (event, minProfit) => {
  const soft = findSoftRow(event && event.bookieList)
  const sharp = findSharpRow(event && event.bookieList)
  if (!soft || !sharp) {
    return []
  }

  const pair = (field) => ([
    { bookmaker: soft.bookmaker, field, price: soft[field] },
    { bookmaker: sharp.bookmaker, field, price: sharp[field] }
  ])

  const markets = []

  const hasDraw = usable(soft.drawPrice) || usable(sharp.drawPrice)
  markets.push({
    name: 'Moneyline',
    line: null,
    outcomes: hasDraw
      ? [
        { name: 'home', candidates: pair('homePrice') },
        { name: 'draw', candidates: pair('drawPrice') },
        { name: 'away', candidates: pair('awayPrice') }
      ]
      : [
        { name: 'home', candidates: pair('homePrice') },
        { name: 'away', candidates: pair('awayPrice') }
      ]
  })

  // a handicap only hedges against the mirror handicap, so the two books must be on the same line
  if (sameLine(soft.spreadPoints, sharp.spreadPoints)) {
    markets.push({
      name: 'Spread',
      line: soft.spreadPoints,
      outcomes: [
        { name: 'home', candidates: pair('homeSpreadPrice') },
        { name: 'away', candidates: pair('awaySpreadPrice') }
      ]
    })
  }

  if (sameLine(soft.totalPoints, sharp.totalPoints)) {
    markets.push({
      name: 'Total',
      line: soft.totalPoints,
      outcomes: [
        { name: 'over', candidates: pair('overPrice') },
        { name: 'under', candidates: pair('underPrice') }
      ]
    })
  }

  const ret = []
  for (const market of markets) {
    const arb = marketArb(market.outcomes)
    if (arb && arb.profit >= minProfit) {
      ret.push({ market: market.name, line: market.line, ...arb })
    }
  }
  return ret.sort((a, b) => b.profit - a.profit)
}

const eventHasArb = (event, minProfit) => findArbs(event, minProfit).length > 0

/**
 * What makes two sightings the same opportunity. The prices are deliberately not in it: a price moving
 * is the same arb on the move, not a new one. The line is, because a total of 8.5 and a total of 9 are
 * different bets.
 */
const arbKey = (event, arb) => [
  event.home,
  event.away,
  event.koTime,
  arb.market,
  arb.line === null || arb.line === undefined ? '' : arb.line,
  arb.legs.map((leg) => `${leg.outcome}@${displayBookmaker(leg.bookmaker)}`).join('+')
].join('|')

module.exports = {
  SOFT_BOOKMAKER,
  SHARP_BOOKMAKER,
  isSoftRow,
  isSharpRow,
  displayBookmaker,
  findSoftRow,
  findSharpRow,
  findArbs,
  eventHasArb,
  arbKey
}
